import type { SimCommand } from "../protocol/commands";
import type { SimEvent } from "../protocol/events";
import type { RejectionCode } from "../protocol/driver";
import type { ValveState } from "../protocol/snapshot";
import { findNode, outgoingEdges, type EngineState } from "./state";

/**
 * Spec 0024: `setValve` sets a `flowMover`'s player-owned routing state — the
 * first Play-phase verb that changes the rig without playing a card, so it is
 * charged in kW like a hardware action rather than a card's cost. Mirrors
 * `setup.ts`'s installHardware/dismantleHardware shape deliberately: every
 * check runs before any mutation, `02-cards-and-economy.md` §2.2's Refused
 * Plays Cost Zero.
 */

export type ValveResult =
  | { ok: true }
  | { ok: false; reason: string; code: RejectionCode; sourceId: string | null };

function refuse(reason: string, code: RejectionCode, sourceId: string | null = null): ValveResult {
  return { ok: false, reason, code, sourceId };
}

/** Resolved Question 1: a flat 1 kW, charged only on success. */
const VALVE_TOGGLE_COST_KW = 1;

export function setValve(args: {
  state: EngineState;
  cmd: Extract<SimCommand, { type: "setValve" }>;
  events: SimEvent[];
}): ValveResult {
  const { state, cmd, events } = args;

  if (state.phase !== "play") {
    return refuse(`setValve only during Play (currently ${state.phase})`, "wrongPhase");
  }

  const node = findNode(state, cmd.nodeId);
  if (!node) return refuse(`no such node: ${cmd.nodeId}`, "wrongNodeKind", cmd.nodeId);
  if (node.kind !== "flowMover") {
    return refuse(`setValve only targets a flowMover, not a ${node.kind}`, "wrongNodeKind", node.id);
  }

  const requestedState = cmd.state;
  if (requestedState.kind === "route") {
    // A route edge must originate at this mover — the One-Hop Rule's board
    // counterpart: a mover can only ever choose among its OWN outgoing pipes.
    const owned = outgoingEdges(state, node.id).some((e) => e.id === requestedState.edgeId);
    if (!owned) {
      return refuse(
        "route edge is not one of this mover's own outgoing edges",
        "invalidPipeEndpoints",
        requestedState.edgeId,
      );
    }
  }

  if (valveStateEquals(node.valveState, requestedState)) {
    return refuse("valve is already set to this state", "noEffect", node.id);
  }

  const remaining = state.energyBudgetKw - state.energySpentKw;
  if (VALVE_TOGGLE_COST_KW > remaining) {
    return refuse("not enough power", "notEnoughPower");
  }

  // A fresh copy, never the command's own object reference — same discipline
  // as cloning a GridPos elsewhere in this codebase.
  const newState: ValveState =
    requestedState.kind === "route" ? { kind: "route", edgeId: requestedState.edgeId } : { kind: requestedState.kind };
  node.valveState = newState;
  state.energySpentKw += VALVE_TOGGLE_COST_KW;
  events.push({ type: "node.valveSet", nodeId: node.id, state: newState, costKw: VALVE_TOGGLE_COST_KW });

  return { ok: true };
}

/**
 * `null` and `{kind:"open"}` are NOT the same value here, even though they
 * behave identically in `pushFromNode` — a player explicitly setting "open"
 * when the mover was left on automatic `null` is a real, chargeable state
 * change. Two `route` settings are equal only when they name the same edge.
 */
function valveStateEquals(current: ValveState | null, next: ValveState): boolean {
  if (current === null) return false;
  if (current.kind !== next.kind) return false;
  if (current.kind === "route" && next.kind === "route") return current.edgeId === next.edgeId;
  return true;
}
